// Manual override — the guaranteed floor. Reads a paste-in localStorage blob
// first (so Matt can drop in live numbers), else the configured JSON file.
// Everything it returns is badged "manual entry" in the UI.
import type { VoteProvider } from '../../types/providers.ts';
import { ProviderError } from '../../types/providers.ts';
import type {
  NormalizedResult,
  CandidateResult,
  CountyResult,
  CallStatus,
} from '../../types/results.ts';
import { readRaw } from '../../lib/storage.ts';
import { nowIso } from '../../lib/time.ts';

interface ManualCandidate {
  id: string;
  votes?: number | null;
  percent?: number | null;
}
interface ManualCounty {
  name: string;
  reportingPercent?: number | null;
  totalVotes?: number | null;
  candidates: ManualCandidate[];
}
interface ManualInput {
  asOf?: string;
  reportingPercent?: number | null;
  estimatedVotesRemaining?: number | null;
  callStatus?: string;
  callFor?: string | null;
  candidates: ManualCandidate[];
  counties?: ManualCounty[];
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function coerceCandidates(list: ManualCandidate[]): CandidateResult[] {
  return list.map((c) => ({ id: String(c.id), votes: num(c.votes), percent: num(c.percent) }));
}
function coerceCall(s: unknown): CallStatus {
  return s === 'projected' || s === 'leaning' ? s : 'not_called';
}

function normalize(input: ManualInput): NormalizedResult {
  if (!input || !Array.isArray(input.candidates)) {
    throw new ProviderError('invalid', 'Manual data is missing a candidates[] array');
  }
  const counties: CountyResult[] = (input.counties ?? []).map((c) => ({
    name: String(c.name),
    reportingPercent: num(c.reportingPercent),
    totalVotes: num(c.totalVotes),
    candidates: coerceCandidates(c.candidates ?? []),
  }));
  return {
    source: 'Manual entry',
    sourceId: 'manual',
    fetchedAt: input.asOf ?? nowIso(),
    reportingPercent: num(input.reportingPercent),
    estimatedVotesRemaining: num(input.estimatedVotesRemaining),
    candidates: coerceCandidates(input.candidates),
    counties,
    callStatus: coerceCall(input.callStatus),
    callFor: input.callFor ?? null,
    entry: 'manual',
  };
}

export const manualProvider: VoteProvider = {
  id: 'manual',
  kind: 'votes',
  async getResults(race, cfg, signal) {
    const pasted = readRaw(`manual:${race.raceId}`);
    if (pasted) {
      try {
        return normalize(JSON.parse(pasted) as ManualInput);
      } catch {
        throw new ProviderError('invalid', 'Pasted manual JSON could not be parsed');
      }
    }
    const src = cfg.manualSource;
    if (!src) throw new ProviderError('not_configured', 'No manual source configured');

    let res: Response;
    try {
      res = await fetch(src, { signal, headers: { accept: 'application/json' } });
    } catch (e) {
      if (signal?.aborted) throw e;
      throw new ProviderError('unreachable', `Could not load ${src}`);
    }
    if (!res.ok) throw new ProviderError('unreachable', `HTTP ${res.status} loading manual data`);
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new ProviderError('invalid', 'Manual data is not valid JSON');
    }
    return normalize(json as ManualInput);
  },
};
